/* eslint-disable max-params, max-statements, max-nested-callbacks, complexity, max-lines */
import {createSelector} from 'reselect';
import {contains, propOr, pathOr, prop, propEq} from 'ramda';

import type {
    ProductType,
    SimpleProductType,
    WindowProductType,
    ProductPriceType,
} from 'constants/flow/flowTypesProduct';
import type {ApplicationStoreType, SelectorType} from 'constants/flow/flowTypes';
import {PRODUCT_TYPES} from 'constants/product';

import {isConfigurePage as isConfigurePageSelector} from 'selectors/routerInfo/routerInfo';
import {isProperty} from 'libs/utils/utils';

import {HOUSE_BRANDS} from 'features/experiments/constants';

export const getProductChildren = store => store.product.children;

/**
 * Takes active children product id
 * @type {SelectorType<?number>}
 */
export const getActiveChildrenProductId: SelectorType<?number> = prop('activeProduct');

/**
 * Takes parent product from store
 * @type {SelectorType<?ProductType>}
 */
export const getParentProduct: SelectorType<?ProductType> = prop('product');

/**
 * Takes active product from products list
 * @type {SelectorType<SimpleProductType>}
 */
export const getActiveChildrenProduct: SelectorType<SimpleProductType> = createSelector(
    getParentProduct,
    getActiveChildrenProductId,
    (product: ProductType, activeProductId: number): SimpleProductType =>
        pathOr({}, ['children', activeProductId], product),
);

/**
 * Get the SKU value for the active product
 * @type {SelectorType<string>}
 */
export const getActiveChildrenProductSku: SelectorType<string> = createSelector(
    getActiveChildrenProduct,
    ({sku}: SimpleProductType): string => sku,
);

/**
 * Determines readers product type of parent product
 * @type {SelectorType<boolean>}
 */
export const isParentProductReaders: SelectorType<boolean> = createSelector(
    getParentProduct,
    propEq('type', PRODUCT_TYPES.READERS),
);

/**
 * Is sunglasses selector
 * @type {SelectorType<boolean>}
 */
export const isSunglassesParentProduct: SelectorType<boolean> = createSelector(
    getParentProduct,
    propEq('type', PRODUCT_TYPES.SUNGLASSES),
);

/**
 * Determines product preloaded state.
 * @type {SelectorType<boolean>}
 */
export const isActiveChildrenProductPreloaded: SelectorType<boolean> = createSelector(
    getParentProduct,
    propOr(false, 'preloaded'),
);

/**
 * Return is readers or not
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} Flag "is readers"
 */
export const isReaders = (store: ApplicationStoreType): boolean =>
    store.product.type === PRODUCT_TYPES.READERS;

export const getSimples = createSelector(getParentProduct, (windowProduct: ProductType): {} => {
    if (windowProduct?.status === 'fulfilled' || windowProduct?.preloaded) {
        return windowProduct.children;
    }

    return false;
});

/**
 * Get active product
 * @returns {ProductType | boolean} Active product
 */
export const getActiveProduct = createSelector(
    getActiveChildrenProductId,
    getSimples,
    propOr(false),
);

/**
 * Returns price of active product
 * @param {ProductPriceType} store - application store
 * @returns {ProductPriceType} price object
 */
export const getActiveProductPrice = createSelector(
    getActiveProduct,
    ({price, finalPrice}: ProductType): ProductPriceType => ({
        regular: price,
        final: finalPrice,
    }),
);

/**
 * Defines active product type: 'sunglasses', 'eyeglasses', 'contact_lenses'
 * @param {ApplicationStoreType} state - store state
 * @return {string} - one of 'sunglasses', 'eyeglasses', 'contact_lenses', 'readers'
 */
export const getActiveProductType = createSelector(getActiveProduct, ({type}): ProductType => type);

/**
 * Returns meta of active product
 * @returns {{description?: string, title?: string, keyword?: string}} meta object
 */
export const getActiveProductMeta = createSelector(
    getActiveProduct,
    ({meta}: ProductType): {description?: string, title?: string, keyword?: string} => meta || {},
);

/**
 * Return is sunglasses or not
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} Flag "is sunglasses"
 */
export const isSunglasses = createSelector(
    getParentProduct,
    getActiveProduct,
    (windowProduct: WindowProductType, activeProduct: SimpleProductType): boolean =>
        windowProduct.type === PRODUCT_TYPES.SUNGLASSES && !activeProduct.isEligibleForRx,
);

/**
 * Return is RxSunglasses or not
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} Flag "is RxSunglasses"
 */
export const isRxSunglasses = createSelector(
    getParentProduct,
    getActiveProduct,
    (windowProduct: WindowProductType, activeProduct: SimpleProductType): boolean =>
        windowProduct.type === PRODUCT_TYPES.SUNGLASSES && activeProduct.isEligibleForRx === true,
);

/**
 * Return is premium flag for active product child
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} Flag "isPremium"
 */
export const isPremium = createSelector(
    getActiveProduct,
    (activeProduct: SimpleProductType): boolean =>
        propOr([], 'badges', activeProduct).some(propEq('id', 127)),
);

/**
 * Return is sale flag for active product child
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} Flag "isSale"
 */
export const isSale = createSelector(
    getActiveProduct,
    (activeProduct: SimpleProductType): boolean =>
        propOr([], 'badges', activeProduct).some(propEq('id', 131)),
);

/**
 * Checking for product badges
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} product contain badges
 */
export const hasProductBadge = createSelector(
    getActiveProduct,
    (activeProduct: SimpleProductType): boolean =>
        Boolean(propOr([], 'badges', activeProduct).length),
);

/**
 * Is active product eligible for anti-fog coating?
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} Flag "isEligibleForPolycarbonate"
 */
export const isEligibleForAntiFog = createSelector(
    getActiveProduct,
    (activeProduct: SimpleProductType): boolean =>
        propOr(false, 'is_eligible_for_anti_fog', activeProduct),
);

/**
 * Returns option template for active product child
 * @param {ApplicationStoreType} store - Current store
 * @returns {string | number | null} option template id for get wizard template
 */
export const getOptionTemplate = createSelector(
    getActiveProduct,
    (activeProduct: SimpleProductType): number => propOr(0, 'wizardId', activeProduct),
);

/**
 * Return is current product contact lenses or not
 * @param {ApplicationStoreType} store - Current store
 * @returns {boolean} Flag "is contact lenses"
 */
export const isContactLenses = createSelector(
    getParentProduct,
    (windowProduct: WindowProductType): boolean =>
        windowProduct.type === PRODUCT_TYPES.CONTACT_LENSES,
);

/**
 * Returns option template for active product child
 * @param {ApplicationStoreType} store - Current store
 * @returns {number} option template id for get wizard template
 */
export const productUseOldWizard = isContactLenses;

/**
 * Determines whether non-rx add to cart button should be shown or shouldn't.
 *
 * @param {ApplicationStoreType} store - store
 * @returns {boolean} true - show the button, false - hide the button
 */
export const hasNonRXAddToCart = createSelector(
    getParentProduct,
    isConfigurePageSelector,
    (product: ProductType, isConfigurePage: boolean): boolean =>
        !isConfigurePage &&
        [PRODUCT_TYPES.READERS, PRODUCT_TYPES.SUNGLASSES].includes(product.type),
);

/**
 * Returns option template for active product child
 * @param {ApplicationStoreType} store - Current store
 * @returns {number} option template id for get wizard template
 */
export const getProductSku = createSelector(
    getActiveProduct,
    (activeProduct: SimpleProductType): number => propOr('', 'sku', activeProduct),
);
/**
 * Get existence of lens feature in active product
 * @param {ApplicationStoreType} store - Application store
 * @param {string} lensFeature - Lens Feature attribute
 * @returns {boolean} - Existence of lens feature
 */
export const hasLensFeature = (store: ApplicationStoreType, lensFeature: string): boolean => {
    const product = getActiveProduct(store);

    if (!product.lensFeatures) return false;

    if (product?.lensFeatures?.label === lensFeature) return true;

    return (
        Array.isArray(product?.lensFeatures) &&
        product.lensFeatures.some(({label}) => label === lensFeature)
    );
};

/**
 * Returns true if the Product belongs to the Kids category.
 * @param {ProductType} product - Product object
 * @returns {ProductReviewsType} Flag if product belongs to the Kids category
 */
export const isKidsGlasses = (product: ProductType): boolean =>
    [contains('Boys', product.genders), contains('Girls', product.genders), product.isKids].some(
        value => value === true,
    );

/**
 * Return status "is sold out" of active product
 * @param {ApplicationStoreType} store - Application store
 * @returns {boolean} Status "is sold out" of active product
 */
export const isSoldOut = createSelector(
    getActiveProduct,
    (activeProduct: SimpleProductType): boolean =>
        Boolean(activeProduct?.stock?.status === 'out_of_stock' || activeProduct?.stock?.qty === 0),
);

/**
 * Get product reviews
 * @param {ApplicationStoreType} store - Application Store
 * @returns {ProductReviewsType} - Product Reviews
 */
export const getProductReviews = (store: ApplicationStoreType): ProductReviewsType =>
    store.productReviews;

/**
 * Get is reviews info loaded
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is loaded
 */
export const getIsReviewsItemsFulfilled = isProperty('productReviews.status', 'fulfilled');

/**
 * Get is product info loaded
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is loaded
 */
export const getIsProductFulfilled = isProperty('product.status', 'fulfilled');

/**
 * Determines is active product eligible for multi focal
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is active product eligible for multi focal
 */
export const isEligibleForMultiFocal = createSelector(
    getActiveProduct,
    (product: SimpleProductType) => !product.isKids,
);

/**
 * Determines is active product eligible for hto
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is active product eligible for hto
 */
export const isEligibleForHto = createSelector(getActiveProduct, propOr(false, 'eligibleForHto'));

/**
 * Determines is active product eligible for ExpPriceOfferHB
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is active product eligible for ExpPriceOfferHB
 */
export const isEligibleForExpPriceOfferHB = createSelector(
    isSale,
    isPremium,
    getActiveProduct,
    (isSaleCategory, isPremiumCategory, brandName): boolean =>
        Boolean(
            !isSaleCategory && !isPremiumCategory && HOUSE_BRANDS.includes(brandName?.brand?.label),
        ),
);
